import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import * as Cookies from 'js-cookie';
import * as OrganizationsSelectors from 'shared_redux/selectors/models/organizations';
import styles from './styles.module.scss';
import BlockTypes from 'constants/blockTypes';

class PublicProgramView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            program: null,
            loading: true,
            error: null,
            amrapTooltips: {},
            isCopyInFlight: false
        };
        this.overlayRef = React.createRef();
        this.footerRef = React.createRef();
    }

    detectMobileOS = () => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;

        // iOS detection
        if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
            return 'iOS';
        }

        // Android detection
        if (/android/i.test(userAgent)) {
            return 'Android';
        }

        // Not mobile or unknown
        return 'desktop';
    }

    handleCopyProgram = () => {
        const { program } = this.state;

        if (!program || !program.is_public) {
            alert('This program is not available for copying.');
            return;
        }

        // Store program data in localStorage for the coaching portal to pick up
        localStorage.setItem('copyProgramData', JSON.stringify({
            programId: program.id,
            programName: program.name,
            sourceOrg: program.organization_id,
            timestamp: Date.now()
        }));

        // Open coaching portal programs page in new tab
        const coachingPortalUrl = window.location.origin + '/programs';
        const newWindow = window.open(coachingPortalUrl, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
    }

    handleMobileDownload = async (appStoreUrl) => {
        const { program, isCopyInFlight } = this.state;
        const isIOS = this.detectMobileOS() === 'iOS';

        // Prevent duplicate requests
        if (isCopyInFlight) {
            return;
        }

        if (!program) {
            if (isIOS) {
                window.location.href = appStoreUrl;
            } else {
                const newWindow = window.open(appStoreUrl, '_blank', 'noopener,noreferrer');
                if (newWindow) newWindow.opener = null;
            }
            return;
        }

        // If user is logged in, copy and schedule the program first
        if (this.props.loggedIn) {
            this.setState({ isCopyInFlight: true });
            try {
                // Generate CSRF token (same pattern as api.js)
                const array = new Uint32Array(1);
                crypto.getRandomValues(array);
                const csrfToken = array[0];

                // Set CSRF cookie
                const inFifteenMinutes = new Date(new Date().getTime() + 15 * 60 * 1000);
                Cookies.set('csrf', csrfToken, {
                    expires: inFifteenMinutes,
                    secure: true,
                    sameSite: 'strict',
                });

                const response = await fetch('/api/programs/copy-and-schedule', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({
                        source_program_id: program.id,
                        csrf: csrfToken
                    })
                });

                if (!response.ok) {
                    console.error('Failed to copy and schedule program');
                }
            } catch (error) {
                console.error('Error copying program:', error);
            } finally {
                // Clean up CSRF cookie regardless of success or error
                Cookies.remove('csrf');
                this.setState({ isCopyInFlight: false });
            }
        }

        // Open app store
        if (isIOS) {
            window.location.href = appStoreUrl;
        } else {
            const newWindow = window.open(appStoreUrl, '_blank', 'noopener,noreferrer');
            if (newWindow) newWindow.opener = null;
        }
    }

    componentDidMount() {
        this.fetchProgram();
        this.hideChatWidget();

        // Update footer margin on window resize
        window.addEventListener('resize', this.updateFooterMargin);

        // Initial footer margin update
        setTimeout(this.updateFooterMargin, 0);
    }

    componentWillUnmount() {
        this.showChatWidget();
        window.removeEventListener('resize', this.updateFooterMargin);
    }

    updateFooterMargin = () => {
        if (this.overlayRef.current && this.footerRef.current) {
            const overlayHeight = this.overlayRef.current.offsetHeight;
            this.footerRef.current.style.marginBottom = `${overlayHeight}px`;
        }
    }

    hideChatWidget = () => {
        // Hide HubSpot chat widget using their API
        if (window.HubSpotConversations) {
            window.HubSpotConversations.widget.remove();
        }

        // Also set up listener in case HubSpot loads after this runs
        window.hsConversationsOnReady = window.hsConversationsOnReady || [];
        window.hsConversationsOnReady.push(() => {
            if (window.HubSpotConversations) {
                window.HubSpotConversations.widget.remove();
            }
        });

        // Fallback CSS in case API doesn't work
        const style = document.createElement('style');
        style.id = 'hide-chat-widget-style';
        style.textContent = `
            #hubspot-messages-iframe-container {
                display: none !important;
                visibility: hidden !important;
            }
        `;
        document.head.appendChild(style);
    }

    showChatWidget = () => {
        // Show HubSpot chat widget using their API
        if (window.HubSpotConversations) {
            window.HubSpotConversations.widget.load();
        }

        // Remove the injected style
        const style = document.getElementById('hide-chat-widget-style');
        if (style) {
            style.remove();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // Check if the program ID in the route has changed
        const currentProgramId = this.props.match.params.programid;
        const previousProgramId = prevProps.match.params.programid;

        if (currentProgramId !== previousProgramId) {
            // Reset state and fetch new program
            this.setState({
                program: null,
                loading: true,
                error: null
            });
            this.fetchProgram();
        }

        // Update footer margin when loading completes, program changes, or login state changes
        if (prevState.loading !== this.state.loading ||
            prevState.program !== this.state.program ||
            prevProps.loggedIn !== this.props.loggedIn) {
            // Use setTimeout to ensure DOM has updated
            setTimeout(this.updateFooterMargin, 0);
        }
    }

    fetchProgram = async () => {
        try {
            const programId = this.props.match.params.programid;
            const response = await fetch(`/api/shared/${programId}`);

            if (response.ok) {
                const program = await response.json();
                this.setState({ program, loading: false });
            } else {
                this.setState({ error: 'Program not found or not public', loading: false });
            }
        } catch (error) {
            this.setState({ error: 'Failed to load program', loading: false });
        }
    }

    toggleAmrapTooltip = (planId) => {
        this.setState(prevState => ({
            amrapTooltips: {
                ...prevState.amrapTooltips,
                [planId]: !prevState.amrapTooltips[planId]
            }
        }));
    }

    getIntensityDisplay = (plan) => {
        // For velocity-based autoregulation, show the target velocity
        if (plan.weight_autoregulation_speed?.autoregulation_target) {
            // autoregulation_metric should indicate if it's velocity
            return ` @ ${plan.weight_autoregulation_speed.autoregulation_target} m/s`;
        }

        // For weight autoregulation, show the autoregulation target if it exists
        if (plan.weight_autoregulation?.autoregulation_target) {
            // This is for RPE or other autoregulation metrics, not the start load
            return '';  // Don't show weight percentages as they're the start load
        }

        // For no autoregulation, only show percentage if it's explicitly set
        if (plan.no_autoregulation?.weight && plan.no_autoregulation?.weight_unit === '%') {
            return ` @ ${plan.no_autoregulation.weight}%`;
        }

        return '';
    }

    renderAmrapElement = (planId) => {
        const showTooltip = this.state.amrapTooltips[planId];
        return (
            <span
                style={{
                    textDecoration: 'underline dotted',
                    cursor: 'help',
                    position: 'relative'
                }}
                onMouseEnter={() => this.setState(prevState => ({
                    amrapTooltips: { ...prevState.amrapTooltips, [planId]: true }
                }))}
                onMouseLeave={() => this.setState(prevState => ({
                    amrapTooltips: { ...prevState.amrapTooltips, [planId]: false }
                }))}
                onClick={() => this.toggleAmrapTooltip(planId)}
            >
                AMRAP
                {showTooltip && (
                    <span style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#333',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        zIndex: 1000,
                        marginBottom: '4px'
                    }}>
                        As Many Reps As Possible
                        <span style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            borderLeft: '4px solid transparent',
                            borderRight: '4px solid transparent',
                            borderTop: '4px solid #333'
                        }}></span>
                    </span>
                )}
            </span>
        );
    }

    render() {
        const { program, loading, error } = this.state;

        if (loading) {
            return (
                <div className={styles.container}>
                    <div className={styles.loading}>Loading program...</div>
                </div>
            );
        }

        if (error) {
            return (
                <div className={styles.container}>
                    <div className={styles.error}>
                        <h1>Program Not Available</h1>
                        <p>{error}</p>
                    </div>
                </div>
            );
        }

        if (!program) {
            return (
                <div className={styles.container}>
                    <div className={styles.error}>
                        <h1>Program Not Found</h1>
                        <p>This program may not exist or is no longer public.</p>
                    </div>
                </div>
            );
        }

        const programUrl = `${window.location.origin}/shared/${program.id}`;
        const microcycleCount = program.microcycles ? program.microcycles.length : 0;
        const description = program.notes
            ? `${program.notes.substring(0, 160)}...`
            : `A training program created with RepOne. ${microcycleCount || ''} ${program.microcycle_name || 'Week'}s of structured training.`;

        return (
            <div className={styles.container}>
                <Helmet>
                    <title>{program.name} - RepOne Training Program</title>
                    <meta name="description" content={description} />
                    <meta name="keywords" content="training program, fitness, workout, RepOne, exercise program" />

                    {/* Open Graph / Facebook */}
                    <meta property="og:type" content="article" />
                    <meta property="og:title" content={`${program.name} - RepOne Training Program`} />
                    <meta property="og:description" content={description} />
                    <meta property="og:url" content={programUrl} />
                    <meta property="og:site_name" content="RepOne" />

                    {/* Twitter */}
                    <meta name="twitter:card" content="summary" />
                    <meta name="twitter:title" content={`${program.name} - RepOne Training Program`} />
                    <meta name="twitter:description" content={description} />

                    {/* Additional SEO */}
                    <meta name="robots" content="index, follow" />
                    <meta name="author" content="RepOne" />
                    <link rel="canonical" href={programUrl} />

                    {/* Structured Data */}
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "ExerciseProgram",
                            "name": program.name,
                            "description": description,
                            "url": programUrl,
                            "author": {
                                "@type": "Organization",
                                "name": "RepOne"
                            },
                            "dateCreated": program.created_on,
                            "applicationCategory": "Fitness"
                        })}
                    </script>
                </Helmet>

                <main className={styles.main}>
                    <div className={styles.section}>
                        <h2>{program.name}</h2>
                        {program.notes ? (
                            <div className={styles.notes}>
                                <p>{program.notes}</p>
                            </div>
                        ) : (
                            <p className={styles.placeholder}>No description available.</p>
                        )}
                    </div>

                    {program.authorInfo && (
                        <div className={styles.authorSectionInline}>
                            {program.authorInfo.avatar ? (
                                <img
                                    src={program.authorInfo.avatar}
                                    alt={program.authorInfo.name}
                                    className={styles.authorAvatarInline}
                                />
                            ) : (
                                <div className={styles.authorAvatarPlaceholderInline}>
                                    {program.authorInfo.name ? program.authorInfo.name.charAt(0).toUpperCase() : '?'}
                                </div>
                            )}
                            <span className={styles.authorNameInline}>{program.authorInfo.name}</span>
                        </div>
                    )}

                    {program.microcycles && program.microcycles.length > 0 && (
                        <div className={styles.section}>
                            {program.microcycles.map((microcycle, microcycleIndex) => (
                                <div key={microcycle.id} className={styles.microcycle}>
                                    <h3>{program.microcycle_name || 'Week'} {microcycleIndex + 1}</h3>
                                    {microcycle.microcycle_sessions && microcycle.microcycle_sessions.map((microcycleSession, sessionIndex) => (
                                        <div key={microcycleSession.id} className={styles.session}>
                                            <h4>Session {sessionIndex + 1}</h4>
                                            {microcycleSession.session && microcycleSession.session.blocks &&
                                             microcycleSession.session.blocks.map((block, blockIndex) => {
                                                if (!block.plans || block.plans.length === 0) return null;

                                                // Determine if it's a superset based on block type
                                                const isSuperset = block.type === BlockTypes.SUPERSET;
                                                const firstPlan = block.plans[0];

                                                // Get sets count from the block
                                                const minSets = block.min_sets || 0;
                                                const maxSets = block.max_sets;

                                                // Format sets display
                                                const getSetsDisplay = () => {
                                                    if (maxSets && minSets !== maxSets) {
                                                        return `${minSets}-${maxSets}`;
                                                    }
                                                    return minSets;
                                                };

                                                // Determine block type based on block properties
                                                const getBlockTypeLabel = () => {
                                                    if (block.type === BlockTypes.ALTERNATING) {
                                                        return 'Alternating';
                                                    } else if (block.type === BlockTypes.SUPERSET) {
                                                        return 'Superset';
                                                    } else {
                                                        return 'Block';
                                                    }
                                                };

                                                return (
                                                    <div key={block.id} className={styles.block}>
                                                        {isSuperset || block.type === BlockTypes.ALTERNATING ? (
                                                            <>
                                                                <div className={styles.supersetHeader}>
                                                                    {getBlockTypeLabel()} x {getSetsDisplay()} sets
                                                                </div>
                                                                <div className={styles.supersetContainer}>
                                                                    {block.plans.map((plan, planIndex) => (
                                                                        <div key={plan.id} className={styles.supersetExercise}>
                                                                            <span className={styles.exerciseText}>
                                                                                {plan.exercise ? plan.exercise.name : 'Exercise'}{' '}
                                                                                {(() => {
                                                                                    const isAmrap = plan.no_autoregulation?.amrap ||
                                                                                                  plan.weight_autoregulation?.amrap ||
                                                                                                  plan.weight_autoregulation_speed?.amrap;

                                                                                    if (isAmrap) {
                                                                                        return this.renderAmrapElement(plan.id);
                                                                                    }

                                                                                    if (plan.no_autoregulation) {
                                                                                        return `${plan.no_autoregulation.num_reps} reps`;
                                                                                    } else if (plan.weight_autoregulation) {
                                                                                        return `${plan.weight_autoregulation.num_reps || 0} reps`;
                                                                                    } else if (plan.weight_autoregulation_speed) {
                                                                                        return `${plan.weight_autoregulation_speed.num_reps || 0} reps`;
                                                                                    }
                                                                                    return 'reps';
                                                                                })()}
                                                                                {this.getIntensityDisplay(plan)}
                                                                                {plan.notes ? `. ${plan.notes}` : ''}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            block.plans.map((plan, planIndex) => (
                                                                <div key={plan.id} className={styles.regularExercise}>
                                                                    <div className={styles.exerciseLine}>
                                                                        <span className={styles.exerciseText}>
                                                                            {plan.exercise ? plan.exercise.name : 'Exercise'}{' '}
                                                                            {(() => {
                                                                                const isAmrap = plan.no_autoregulation?.amrap ||
                                                                                              plan.weight_autoregulation?.amrap ||
                                                                                              plan.weight_autoregulation_speed?.amrap;

                                                                                if (plan.no_autoregulation) {
                                                                                    return `${plan.no_autoregulation.min_sets} sets x ${isAmrap ? '' : plan.no_autoregulation.num_reps + ' reps'}${isAmrap ? '' : ''}`;
                                                                                } else if (plan.weight_autoregulation) {
                                                                                    return `${plan.weight_autoregulation.min_sets || 0} sets x ${isAmrap ? '' : (plan.weight_autoregulation.num_reps || 0) + ' reps'}`;
                                                                                } else if (plan.weight_autoregulation_speed) {
                                                                                    return `${plan.weight_autoregulation_speed.min_sets || 0} sets x ${isAmrap ? '' : (plan.weight_autoregulation_speed.num_reps || 0) + ' reps'}`;
                                                                                }
                                                                                return 'sets x reps';
                                                                            })()} {(() => {
                                                                                const isAmrap = plan.no_autoregulation?.amrap ||
                                                                                              plan.weight_autoregulation?.amrap ||
                                                                                              plan.weight_autoregulation_speed?.amrap;

                                                                                if (isAmrap) {
                                                                                    return this.renderAmrapElement(plan.id);
                                                                                }
                                                                                return '';
                                                                            })()}
                                                                            {this.getIntensityDisplay(plan)}
                                                                            {plan.notes ? `. ${plan.notes}` : ''}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}

                    {program.authorInfo && (
                        <div className={styles.authorSection}>
                            <div className={styles.authorContent}>
                                <div className={styles.authorImage}>
                                    {program.authorInfo.avatar ? (
                                        <img
                                            src={program.authorInfo.avatar}
                                            alt={program.authorInfo.name}
                                            className={styles.authorAvatar}
                                        />
                                    ) : (
                                        <div className={styles.authorAvatarPlaceholder}>
                                            {program.authorInfo.name ? program.authorInfo.name.charAt(0).toUpperCase() : '?'}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.authorInfo}>
                                    <h3 className={styles.authorName}>{program.authorInfo.name}</h3>
                                    {program.authorInfo.role && (
                                        <p className={styles.authorTitle}>{program.authorInfo.role}</p>
                                    )}
                                    {program.authorInfo.bio && (
                                        <p className={styles.authorBio}>{program.authorInfo.bio}</p>
                                    )}
                                    {program.contact_email && (
                                        <a
                                            href={`mailto:${program.contact_email}`}
                                            className={styles.coachingButton}
                                        >
                                            Get coaching from {program.authorInfo.name}
                                        </a>
                                    )}

                                    {/* Social Media Links */}
                                    {(program.authorInfo.instagram || program.authorInfo.tiktok || program.authorInfo.facebook || program.authorInfo.twitter) && (
                                        <div className={styles.socialLinks}>
                                            {program.authorInfo.instagram && (
                                                <a
                                                    href={`https://instagram.com/${program.authorInfo.instagram}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.socialLink}
                                                    aria-label="Instagram"
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                                    </svg>
                                                </a>
                                            )}
                                            {program.authorInfo.tiktok && (
                                                <a
                                                    href={`https://tiktok.com/@${program.authorInfo.tiktok}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.socialLink}
                                                    aria-label="TikTok"
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-.88-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                                                    </svg>
                                                </a>
                                            )}
                                            {program.authorInfo.facebook && (
                                                <a
                                                    href={`https://facebook.com/${program.authorInfo.facebook}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.socialLink}
                                                    aria-label="Facebook"
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                                    </svg>
                                                </a>
                                            )}
                                            {program.authorInfo.twitter && (
                                                <a
                                                    href={`https://twitter.com/${program.authorInfo.twitter}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.socialLink}
                                                    aria-label="Twitter"
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                                                    </svg>
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                </main>

                <div className={styles.appDownloadOverlay} ref={this.overlayRef}>
                    <div className={styles.overlayContent}>
                        {(() => {
                            const mobileOS = this.detectMobileOS();
                            const isMobile = mobileOS === 'iOS' || mobileOS === 'Android';

                            // On mobile, if not logged in, show login/signup buttons
                            if (isMobile && !this.props.loggedIn) {
                                const redirectPath = encodeURIComponent(`/shared/${program.id}`);
                                return (
                                    <>
                                        <h3 className={styles.overlayTitle}>Login to your free account to start this program now.</h3>
                                        <div className={styles.authButtons}>
                                            <Link to={`/signup?redirect=${redirectPath}&program_id=${program.id}`} className={styles.authButton}>Sign Up</Link>
                                            <Link to={`/login?redirect=${redirectPath}`} className={styles.authButton}>Login</Link>
                                        </div>
                                    </>
                                );
                            }

                            // On mobile logged in, show only the relevant OS download button
                            if (isMobile) {
                                const iosUrl = `https://apps.apple.com/us/app/repone-athlete/id1608123039?mt=8&ct=program_${program.id}`;
                                const androidUrl = `https://play.google.com/store/apps/details?id=com.reponestrength.personal&referrer=utm_source%3Dprogram%26utm_medium%3Dweb%26utm_campaign%3Dprogram_${program.id}`;

                                return (
                                    <>
                                        <div className={styles.downloadButtons}>
                                            <button
                                                className={styles.downloadButton}
                                                onClick={() => this.handleMobileDownload(iosUrl)}
                                            >
                                                Start This Program Now
                                            </button>
                                        </div>
                                        <div className={styles.overlaySubtitle}>Get workout guidance, save progress, and more <em>for free</em> in the RepOne Athlete app.</div>
                                    </>
                                );

                                return (
                                    <>
                                        <h3 className={styles.overlayTitle}>Get the app to start your workout!</h3>
                                        <div className={styles.downloadButtons}>
                                            {mobileOS === 'iOS' ? (
                                                <button
                                                    onClick={() => this.handleMobileDownload(iosUrl)}
                                                    className={styles.appButton}
                                                >
                                                    <img
                                                        src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1159056000"
                                                        alt="Download on the App Store"
                                                    />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => this.handleMobileDownload(androidUrl)}
                                                    className={`${styles.appButton} ${styles.googlePlayButton}`}
                                                >
                                                    <img
                                                        src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                                                        alt="Get it on Google Play"
                                                    />
                                                </button>
                                            )}
                                        </div>
                                    </>
                                );
                            }

                            // Desktop - show full layout with both columns
                            return (
                                <>
                                    <h3 className={styles.overlayTitle}>Try out this workout program</h3>
                                    <div className={styles.overlayColumns}>
                                        <div className={styles.coachingColumn}>
                                            <p>Our coaching portal allows you to program to many athletes</p>
                                            <button
                                                onClick={this.handleCopyProgram}
                                                className={styles.copyButton}
                                            >
                                                Copy Workout Program
                                            </button>
                                        </div>
                                        <div className={styles.mobileColumn}>
                                            <p>RepOne Athlete for mobile guides you through each workout step by step</p>
                                            <div className={styles.downloadButtons}>
                                                {mobileOS === 'iOS' ? (
                                                    <a
                                                        href={`https://apps.apple.com/us/app/repone-athlete/id1608123039?mt=8&ct=program_${program.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={styles.appButton}
                                                    >
                                                        <img
                                                            src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1159056000"
                                                            alt="Download on the App Store"
                                                        />
                                                    </a>
                                                ) : mobileOS === 'Android' ? (
                                                    <a
                                                        href={`https://play.google.com/store/apps/details?id=com.reponestrength.personal&referrer=utm_source%3Dprogram%26utm_medium%3Dweb%26utm_campaign%3Dprogram_${program.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={`${styles.appButton} ${styles.googlePlayButton}`}
                                                    >
                                                        <img
                                                            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                                                            alt="Get it on Google Play"
                                                        />
                                                    </a>
                                                ) : (
                                                    <>
                                                        <a
                                                            href={`https://apps.apple.com/us/app/repone-athlete/id1608123039?mt=8&ct=program_${program.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={styles.appButton}
                                                        >
                                                            <img
                                                                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1159056000"
                                                                alt="Download on the App Store"
                                                            />
                                                        </a>
                                                        <a
                                                            href={`https://play.google.com/store/apps/details?id=com.reponestrength.personal&referrer=utm_source%3Dprogram%26utm_medium%3Dweb%26utm_campaign%3Dprogram_${program.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={`${styles.appButton} ${styles.googlePlayButton}`}
                                                        >
                                                            <img
                                                                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                                                                alt="Get it on Google Play"
                                                            />
                                                        </a>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    loggedIn: OrganizationsSelectors.getIsLoggedIn(state),
});

export default connect(mapStateToProps)(PublicProgramView);
